import { Suspense, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Group,
  ActionIcon,
  Skeleton,
  Tooltip,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { IconTrash, IconSettings } from '@tabler/icons-react';
import { Paper } from '../../../ui';
import { getWidget } from '../registry';
import { useDashboardStore } from '../../../../stores/dashboardStore';
import { useDashboardContext } from '../context/DashboardProvider';
import { ContainerChild, DashboardGlobalData, WidgetProps, WidgetSettings } from '../types';
import { pickTextColorForBackground } from '../../../../utils/color';
import { usePreferencesStore } from '../../../../stores/preferencesStore';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// Theme Styling Utilities
// ============================================================================

interface ThemeStyles {
  background?: string;
  color?: string;
  border?: string;
  boxShadow?: string;
}

function getThemeStyles(
  settings: WidgetSettings | undefined,
  colorScheme: 'light' | 'dark',
  primaryColor: string
): ThemeStyles {
  const themeVariant = settings?.themeVariant || 'default';

  switch (themeVariant) {
    case 'gradient': {
      const gradientFrom = primaryColor;
      const gradientTo = colorScheme === 'dark' ? '#1a1b1e' : '#f8f9fa';
      return {
        background: `linear-gradient(135deg, ${gradientFrom}22, ${gradientTo})`,
      };
    }
    case 'minimal': {
      return {
        background: 'transparent',
        border: 'none',
        boxShadow: 'none',
      };
    }
    case 'custom': {
      const bgColor = settings?.backgroundColor;
      const gradientColor = settings?.backgroundGradient;

      if (!bgColor) return {};

      const background = gradientColor
        ? `linear-gradient(135deg, ${bgColor}, ${gradientColor})`
        : bgColor;

      const textColor =
        settings?.textColor === 'auto' ? pickTextColorForBackground(bgColor) : settings?.textColor;

      return {
        background,
        color: textColor,
      };
    }
    default:
      return {};
  }
}

// ============================================================================
// Loading Skeleton
// ============================================================================

function WidgetLoader() {
  return (
    <Box p="sm" style={{ height: '100%' }}>
      <Skeleton height={20} width="60%" mb="sm" />
      <Skeleton height={40} mb="sm" />
      <Skeleton height={20} width="40%" />
    </Box>
  );
}

// ============================================================================
// ChildWidgetRenderer Component
// ============================================================================

export interface ChildWidgetRendererProps {
  /** Container instance ID */
  containerInstanceId: string;
  /** Child widget data */
  child: ContainerChild;
  /** Global dashboard data */
  globalData?: DashboardGlobalData;
  /** Whether container is in editing mode */
  isEditing?: boolean;
  /** Whether global dashboard edit mode is active */
  isGlobalEditMode?: boolean;
}

export function ChildWidgetRenderer({
  containerInstanceId,
  child,
  globalData,
  isEditing = false,
  isGlobalEditMode = false,
}: ChildWidgetRendererProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const widget = getWidget(child.widgetId);
  const [isHovered, setIsHovered] = useState(false);

  // Get primary color from preferences store
  const colorSchemeData = usePreferencesStore((state) => state.getColorScheme());
  const primaryColor = colorSchemeData?.primary?.from || theme.primaryColor;
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Get theme-specific styles
  const themeStyles = useMemo(
    () => getThemeStyles(child.settings, colorScheme, primaryColor),
    [child.settings, colorScheme, primaryColor]
  );

  // Store actions
  const removeWidgetFromContainer = useDashboardStore(
    (state) => state.removeWidgetFromContainer
  );
  const updateContainerChildSettings = useDashboardStore(
    (state) => state.updateContainerChildSettings
  );

  // Store actions for settings modal
  const openContainerChildSettingsModal = useDashboardStore(
    (state) => state.openContainerChildSettingsModal
  );

  // Handle remove widget from container
  const handleRemove = useCallback(() => {
    removeWidgetFromContainer(containerInstanceId, child.instanceId);
  }, [containerInstanceId, child.instanceId, removeWidgetFromContainer]);

  // Handle open settings modal
  const handleOpenSettings = useCallback(() => {
    openContainerChildSettingsModal(containerInstanceId, child.instanceId);
  }, [containerInstanceId, child.instanceId, openContainerChildSettingsModal]);

  if (!widget) {
    return (
      <Box
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.colors.gray[5],
          backgroundColor:
            colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
          borderRadius: theme.radius.md,
        }}
      >
        Widget not found
      </Box>
    );
  }

  // Compute the actual text color if auto is set
  const computedTextColor =
    child.settings?.themeVariant === 'custom' &&
    child.settings?.textColor === 'auto' &&
    child.settings?.backgroundColor
      ? pickTextColorForBackground(child.settings.backgroundColor)
      : child.settings?.textColor;

  // Build widget props with computed text color
  const widgetProps: WidgetProps = {
    widgetId: child.widgetId,
    instanceId: child.instanceId,
    isCollapsed: false, // Children in containers are never collapsed
    globalData,
    filters: undefined, // Child widgets don't have their own filters
    settings: {
      ...child.settings,
      // Pass computed text color so widgets can use it
      computedTextColor,
    },
  };

  // Get the widget component
  const WidgetComponent = widget.component;

  // Create CSS variables for color inheritance
  const colorVars = themeStyles.color
    ? {
        '--widget-text-color': themeStyles.color,
        color: themeStyles.color,
      }
    : {};

  return (
    <Paper
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        ...themeStyles,
        ...colorVars,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Edit mode controls - show in container edit mode or when hovering in global edit mode */}
      {(isEditing || isGlobalEditMode) && isHovered && (
        <Box
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 10,
            display: 'flex',
            gap: 4,
            backgroundColor:
              colorScheme === 'dark'
                ? 'rgba(0, 0, 0, 0.7)'
                : 'rgba(255, 255, 255, 0.9)',
            borderRadius: theme.radius.sm,
            padding: 2,
          }}
        >
          <Tooltip label="Widget settings" withArrow position="top">
            <ActionIcon size="xs" variant="subtle" color="blue" onClick={handleOpenSettings}>
              <IconSettings size={12} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Remove from container" withArrow position="top">
            <ActionIcon size="xs" variant="subtle" color="red" onClick={handleRemove}>
              <IconTrash size={12} />
            </ActionIcon>
          </Tooltip>
        </Box>
      )}

      {/* Widget content */}
      <Box
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          minHeight: 0,
          padding: theme.spacing.xs,
          color: 'inherit',
        }}
      >
        <Suspense fallback={<WidgetLoader />}>
          <WidgetComponent {...widgetProps} />
        </Suspense>
      </Box>
    </Paper>
  );
}

export default ChildWidgetRenderer;
